import React from 'react';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function collectTextChunks(value: unknown): string[] {
  if (value == null) return [];

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectTextChunks(item));
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const text = String(value).trim();
    return text ? [text] : [];
  }

  if (isRecord(value)) {
    const preferredKeys = ['text', 'value', 'content', 'description', 'details', 'summary'];
    const chunks: string[] = [];

    for (const key of preferredKeys) {
      if (key in value) {
        chunks.push(...collectTextChunks(value[key]));
      }
    }

    const listLikeKeys = ['items', 'list', 'lines', 'points', 'bullets'];
    for (const key of listLikeKeys) {
      if (key in value) {
        const item = value[key];
        if (Array.isArray(item)) {
          chunks.push(...item.flatMap((entry) => collectTextChunks(entry).map((line) => `- ${line}`)));
        } else {
          chunks.push(...collectTextChunks(item));
        }
      }
    }

    if (chunks.length > 0) return chunks;

    return Object.entries(value)
      .flatMap(([key, entry]) => collectTextChunks(entry).map((line) => `${key}: ${line}`));
  }

  return [];
}

export function normalizeMedicalMarkdownInput(value: unknown): string {
  const chunks = collectTextChunks(value);
  return chunks.join('\n').trim();
}

export function cleanMedicalMarkdownText(value: unknown): string {
  const raw = normalizeMedicalMarkdownInput(value);
  if (!raw) return '';

  let text = raw;

  text = text
    .replace(/\r\n/g, '\n')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*(?:[-*•]+|\d+\.)\s+/gm, '- ')
    .replace(/^\s*([*_ -])\1\1+\s*$/gm, '')
    .replace(/\u200b/g, '')
    .replace(/[ \t]+$/gm, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return text;
}

function renderInlineSegments(line: string) {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={index} className="font-semibold text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }

    return <React.Fragment key={index}>{part.replace(/\*\*/g, '')}</React.Fragment>;
  });
}

export function renderInlineMedicalMarkdown(value: unknown, className = '') {
  const text = cleanMedicalMarkdownText(value);
  if (!text) return null;

  return text.split('\n').map((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) {
      return <div key={index} className="h-2" />;
    }

    const bulletMatch = trimmed.match(/^[-•]\s+(.*)$/);
    if (bulletMatch) {
      return (
        <div key={index} className="flex items-start gap-2">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-80" />
          <span className={className}>{renderInlineSegments(bulletMatch[1])}</span>
        </div>
      );
    }

    const orderedMatch = trimmed.match(/^(\d+)\.\s+(.*)$/);
    if (orderedMatch) {
      return (
        <div key={index} className="flex items-start gap-2">
          <span className="min-w-6 shrink-0 font-semibold text-cyan-300">{orderedMatch[1]}.</span>
          <span className={className}>{renderInlineSegments(orderedMatch[2])}</span>
        </div>
      );
    }

    return (
      <p key={index} className={className}>
        {renderInlineSegments(trimmed)}
      </p>
    );
  });
}
